/**
 * Fluxos que passam pela tela do Keycloak (Authorization Code + PKCE).
 *
 * O login do dia a dia é o formulário da SPA (grant `password`, ver
 * oidc.ts). Estes três não cabem nele:
 *
 *   - login social: Google/Apple autenticam no site deles;
 *   - "Esqueceu a senha?": o Keycloak manda o link por e-mail;
 *   - "Alterar senha": o Keycloak pede a senha atual e grava a nova.
 *
 * Os três saem por `signinRedirect` e voltam por /auth/callback, com o
 * `state` (`returnTo`) que leva a pessoa de volta à página de origem — o
 * mesmo caminho que o AuthProvider já trata. A senha nunca passa pela SPA.
 */
import type { SigninRedirectArgs } from 'oidc-client-ts';
import type { SocialProvider } from '@/core/config';
import { CALLBACK_PATH, safeReturnTo, userManager, type SigninState } from './oidc';

/** O portal só fala português: as telas do Keycloak também. */
const UI_LOCALES = 'pt-BR';

/** Ação pedida ao Keycloak junto com o login (Application Initiated Action). */
export const UPDATE_PASSWORD_ACTION = 'UPDATE_PASSWORD';

function state(returnTo: string | undefined): SigninState {
  return { returnTo: safeReturnTo(returnTo) };
}

/** Argumentos do login social: `kc_idp_hint` pula a tela do Keycloak e vai direto ao provedor. */
export function socialSignInArgs(provider: SocialProvider, returnTo?: string): SigninRedirectArgs {
  return {
    state: state(returnTo),
    ui_locales: UI_LOCALES,
    extraQueryParams: { kc_idp_hint: provider },
  };
}

/**
 * Argumentos do "Esqueceu a senha?".
 *
 * Não existe URL pública que abra a redefinição de senha DENTRO de um
 * login iniciado pela SPA (o `tab_id` da sessão de autenticação só nasce no
 * servidor). Por isso o login começa normalmente e `centralone_action`
 * avisa o tema `centralone` do Keycloak (js/centralone.js) para seguir
 * direto para a tela "Redefinir senha". Ao fim — e-mail, link, nova senha
 * — o Keycloak conclui o login original e volta para /auth/callback.
 *
 * `prompt=login`: mesmo com sessão aberta no Keycloak, mostra a tela (sem
 * isso ele devolveria a pessoa logada, sem redefinir nada).
 */
export function passwordResetArgs(loginHint?: string, returnTo?: string): SigninRedirectArgs {
  return {
    state: state(returnTo),
    prompt: 'login',
    login_hint: loginHint?.trim() || undefined,
    ui_locales: UI_LOCALES,
    extraQueryParams: { centralone_action: 'reset-password' },
  };
}

/**
 * Argumentos do "Alterar senha" (logado): `kc_action=UPDATE_PASSWORD`.
 * O Keycloak confirma a senha atual (o login da SPA não abre sessão no
 * navegador), mostra "Crie uma nova senha" e volta com
 * `kc_action_status=success|cancelled` — ver `peekAccountActionResult`.
 */
export function passwordChangeArgs(returnTo: string, loginHint?: string): SigninRedirectArgs {
  return {
    state: state(returnTo),
    login_hint: loginHint?.trim() || undefined,
    ui_locales: UI_LOCALES,
    extraQueryParams: { kc_action: UPDATE_PASSWORD_ACTION },
  };
}

export function signInWithProvider(provider: SocialProvider, returnTo?: string): Promise<void> {
  return userManager.signinRedirect(socialSignInArgs(provider, returnTo));
}

export function startPasswordReset(loginHint?: string, returnTo?: string): Promise<void> {
  return userManager.signinRedirect(passwordResetArgs(loginHint, returnTo));
}

export function startPasswordChange(returnTo: string, loginHint?: string): Promise<void> {
  return userManager.signinRedirect(passwordChangeArgs(returnTo, loginHint));
}

// ─────────────── resultado da ação, na volta do Keycloak ───────────────

export type AccountActionStatus = 'success' | 'cancelled' | 'error';

export interface AccountActionResult {
  action: string;
  status: AccountActionStatus;
}

/**
 * Lê `kc_action`/`kc_action_status` da URL de retorno. Sucesso e
 * cancelamento voltam com `code`: o login se completa igual nos dois casos,
 * só a mensagem muda.
 */
export function readAccountActionResult(
  location: Pick<Location, 'pathname' | 'search'>,
): AccountActionResult | null {
  if (location.pathname !== CALLBACK_PATH) return null;
  const params = new URLSearchParams(location.search);
  const action = params.get('kc_action');
  const status = params.get('kc_action_status');
  if (!action || (status !== 'success' && status !== 'cancelled' && status !== 'error')) return null;
  return { action, status };
}

// Capturado no carregamento do módulo: o AuthProvider troca o `code` e
// reescreve a URL (onSigninCallback) antes de qualquer tela montar.
let pending: AccountActionResult | null =
  typeof window === 'undefined' ? null : readAccountActionResult(window.location);

/** O resultado pendente, sem consumir (o StrictMode chama inicializadores duas vezes). */
export function peekAccountActionResult(): AccountActionResult | null {
  return pending;
}

/** Descarta o resultado depois de mostrado — o aviso não reaparece ao navegar. */
export function clearAccountActionResult(): void {
  pending = null;
}
